import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Mailbox } from '../../types';
import { Button, Card } from '../ui';
import { SyncStatus } from './SyncStatus';
import { useReassignMailbox, useStaff, useSyncMailbox } from '../../api/mailboxes';
import { useAuth } from '../../context/AuthContext';
import { canManageMailboxOwner } from '../../lib/mailbox-roles';
import { getErrorMessage } from '../../lib/error-messages';

interface MailboxCardProps {
  mailbox: Mailbox;
  /*
   * `lastSyncAt` убран 08.09. Страница считала максимум по папкам и
   * передавала одно число — то есть карточка показывала ЛУЧШУЮ из папок и
   * прятала сорвавшиеся. Состояние берётся из самих папок.
   */
  onOpen?: () => void;
}

export function MailboxCard({ mailbox, onOpen }: MailboxCardProps): JSX.Element {
  const navigate = useNavigate();
  const syncMutation = useSyncMailbox();
  const reassignMutation = useReassignMailbox();
  const { user } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  // Список УПРАВЛЯЕМЫЙ. С `defaultValue` поле монтируется до прихода
  // сотрудников — с пустым списком опций, и когда они приезжают, браузер
  // выбирает первого, а не текущего владельца. Клик по нему события не
  // даёт, и передача молча не происходит
  const [selectedOwner, setSelectedOwner] = useState(mailbox.ownerId);

  // Передавать ящик может тот, кто видит все ящики организации
  const canReassign = canManageMailboxOwner(user?.role);
  const {
    data: staff,
    isError: staffFailed,
    isLoading: staffLoading,
  } = useStaff(canReassign && isReassigning, user?.orgId ?? undefined);

  const handleReassign = async (ownerId: string) => {
    setReassignError(null);
    try {
      await reassignMutation.mutateAsync({ mailboxId: mailbox.id, ownerId });
      setIsReassigning(false);
    } catch (err) {
      setReassignError(getErrorMessage(err));
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncError(null);
    try {
      await syncMutation.mutateAsync(mailbox.id);
    } catch (err) {
      setSyncError(getErrorMessage(err));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    } else {
      navigate('/inbox?channel=email');
    }
  };

  return (
    <Card className="border border-border-subtle hover:border-border-strong hover:shadow-medium transition-all duration-150">
      <div className="flex items-start justify-between gap-4">
        {/* Информация о ящике */}
        <div className="flex items-start gap-4 min-w-0 flex-1">
          {/* Иконка */}
          <div className="flex-shrink-0 w-12 h-12 rounded-comp bg-surface-subtle flex items-center justify-center">
            <svg
              className="w-5 h-5 text-text-secondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          </div>

          {/* Инфо */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-text-primary truncate">
                {mailbox.name}
              </h3>
              {/* Индикатор статуса */}
              <span
                className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${mailbox.isActive ? 'bg-accent-success shadow-[0_0_8px_rgba(16,185,129,0.4)]' : 'bg-text-muted'
                  }`}
                title={mailbox.isActive ? 'Активен' : 'Неактивен'}
              />
            </div>
            <p className="text-sm text-text-secondary truncate mt-0.5">
              {mailbox.email}
            </p>
            {/* Менеджер: диалоги ящика видит он, владелец и админ */}
            <p className="text-xs text-text-secondary mt-1">
              Менеджер:{' '}
              <span className="text-text-primary">{mailbox.ownerLogin ?? '—'}</span>
              {canReassign && !isReassigning && (
                <button
                  type="button"
                  className="ml-2 text-accent-brand hover:underline"
                  onClick={() => {
                    // Сброс при открытии: между открытием и нажатием список
                    // мог перезапроситься, и старое значение откатило бы
                    // чужую передачу
                    setSelectedOwner(mailbox.ownerId);
                    setIsReassigning(true);
                  }}
                >
                  Передать
                </button>
              )}
            </p>
            {isReassigning && (
              <div className="mt-2 flex items-center gap-2">
                <select
                  className="text-xs bg-surface-subtle border border-border-subtle rounded-comp px-2 py-1 text-text-primary"
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  disabled={reassignMutation.isPending || !staff}
                >
                  {(staff ?? [{ id: mailbox.ownerId, login: mailbox.ownerLogin ?? '—' }]).map(
                    (person) => (
                      <option key={person.id} value={person.id}>
                        {person.login}
                      </option>
                    )
                  )}
                </select>
                <button
                  type="button"
                  className="text-xs text-accent-brand hover:underline disabled:opacity-50"
                  onClick={() => void handleReassign(selectedOwner)}
                  disabled={reassignMutation.isPending || selectedOwner === mailbox.ownerId}
                >
                  {reassignMutation.isPending ? 'Передаю…' : 'Передать'}
                </button>
                <button
                  type="button"
                  className="text-xs text-text-secondary hover:underline"
                  onClick={() => {
                    setIsReassigning(false);
                    setReassignError(null);
                  }}
                >
                  Отмена
                </button>
              </div>
            )}
            {/* Тихого отказа быть не должно: без этой строки владелец видел
                бы открытую панель, в которой ничего нельзя сделать, и ни
                слова почему */}
            {isReassigning && staffFailed && (
              <p className="text-xs text-accent-danger mt-1">
                Не удалось загрузить список менеджеров. Закройте и попробуйте снова.
              </p>
            )}
            {isReassigning && staffLoading && (
              <p className="text-xs text-text-secondary mt-1">Загружаю менеджеров…</p>
            )}
            {reassignError && (
              <p className="text-xs text-accent-danger mt-1">{reassignError}</p>
            )}
            <div className="mt-2">
              <SyncStatus isSyncing={isSyncing} folders={mailbox.folders} />
            </div>
            {syncError && (
              <p className="text-xs text-accent-danger mt-1">{syncError}</p>
            )}
          </div>
        </div>

        {/* Действия */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing || !mailbox.isActive}
          >
            {isSyncing ? (
              <span className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Синхронизация
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
                Синхронизировать
              </span>
            )}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleOpen}
            disabled={!mailbox.isActive}
          >
            <span className="flex items-center gap-1.5">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
              Открыть
            </span>
          </Button>
        </div>
      </div>
    </Card>
  );
}
